import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Skeleton, Typography, styled } from "@mui/material";
import {
  DocumentProps,
  PrescriptionStatus,
  PrescriptionType,
  PrescriptionPayload,
  RequestType,
  PrescriptionTypeMapper,
} from "@interfaces";
import { PDFViewer, PDFViewerProps, SecondaryActionButton } from "@components";
import { PrescriptionDetailCard } from "../../PrescriptionForm/components/PrescriptionDetailCard";
import { useGetPDFs } from "@hooks";
import { generatePdfName } from "@utils";

import { PrescriptionStatusLabel } from "@components/PrescriptionStatusLabel";

const SummaryContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const Row = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
});

const SectionTitle = styled(Typography)({
  display: "flex",
  fontWeight: 600,
  gap: "8px",
});

const PrescriptionDetailsContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  margin: "8px 0",
});

const ButtonContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  gap: "8px",
});

const ShowPrescriptionButton = styled(SecondaryActionButton)({
  padding: "4px",
});

const SkeletonContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  marginTop: "24px",
  gap: "24px",
});

interface ReducedPrescriptionData {
  prescriptionId: string;
  requestId: string;
  pdfUrl: string;
  prescriptionType: PrescriptionType;
  prescriptionStatus: PrescriptionStatus;
  holdingDate?: number;
}

interface Props {
  data: PrescriptionPayload;
  isFetching: boolean;
}

export const HistoricPrescription = ({ data, isFetching }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: requestId, patient, documents: prescriptions } = data;
  const [documentData, setDocumentData] = useState<DocumentProps>(
    {} as DocumentProps,
  );

  const {
    data: pdfData,
    isPending,
    isSuccess,
    isError,
  } = useGetPDFs({
    requestId,
  });

  const reducedPrescriptionData: Record<string, ReducedPrescriptionData> =
    useMemo(
      () =>
        prescriptions.reduce(
          (
            reducedPrescription,
            { id, type: prescriptionType, prescriptionStatus, holdingDate },
          ) => {
            const treatmentPdfUrl = pdfData?.treatment?.url;

            if (treatmentPdfUrl) {
              reducedPrescription[requestId] = {
                pdfUrl: treatmentPdfUrl,
                prescriptionType,
                prescriptionStatus,
                holdingDate,
                prescriptionId: id,
                requestId,
              };
            } else {
              const pdfUrl = pdfData?.documents
                ?.map(({ url }) => url)
                .find((url) => url.includes(id));

              if (pdfUrl) {
                reducedPrescription[id] = {
                  pdfUrl,
                  prescriptionType,
                  prescriptionStatus,
                  holdingDate,
                  prescriptionId: id,
                  requestId,
                };
              }
            }
            return reducedPrescription;
          },
          {} as Record<string, ReducedPrescriptionData>,
        ),
      [prescriptions, pdfData, requestId],
    );

  const onClose = () => setDocumentData({} as DocumentProps);

  if (Object.keys(documentData).length !== 0) {
    const prevRoute = location.pathname;
    if (isPending) {
      return <PDFViewer.Skeleton prevRoute={prevRoute} />;
    } // TODO: If isError, maybe add a modal with an error message
    else if (isError) {
      onClose();
    } else if (isSuccess) {
      const selectedPrescription =
        reducedPrescriptionData[documentData.documentId];
      const selectedPrescriptionTitle =
        selectedPrescription.prescriptionType === "SIMPLE"
          ? "Receta simple"
          : "Receta retenida";
      const navigationState: PDFViewerProps = {
        documentProps: {
          ...documentData,
          documentUrl: selectedPrescription.pdfUrl,
        },
        pdfRenderingProps: {
          title: selectedPrescriptionTitle,
          prevRoute,
        },
        prescriptionData: {
          requestId: selectedPrescription.requestId,
          prescriptionId: selectedPrescription.prescriptionId,
          prescriptionStatus: selectedPrescription.prescriptionStatus,
          holdingDate: selectedPrescription.holdingDate,
          type: selectedPrescription.prescriptionType ?? undefined,
          isFetching,
          hasMoreThanOnePrescription: Boolean(
            prescriptions.length > 1 && pdfData?.treatment?.url,
          ),
        },
      };
      navigate("/app/solicitudes/visualizacion-documento", {
        state: navigationState,
      });
    }
  }

  return (
    <SummaryContainer>
      {pdfData && pdfData.treatment?.url && (
        <ButtonContainer alignSelf="flex-end">
          <ShowPrescriptionButton
            onClick={() =>
              setDocumentData({
                ...documentData,
                documentId: requestId,
                documentName: generatePdfName({
                  documentType: RequestType.PRESCRIPTION,
                  patient,
                }),
              })
            }
            disabled={isError || !reducedPrescriptionData[requestId]?.pdfUrl}
          >
            Ver tratamiento
          </ShowPrescriptionButton>
        </ButtonContainer>
      )}
      <div>
        {prescriptions.map(({ id, details, type, prescriptionStatus }) => {
          const title = type === "SIMPLE" ? "Receta simple" : "Receta retenida";
          return (
            <Box key={`${title}-${id}`}>
              <Row>
                <SectionTitle>{title}</SectionTitle>
                <Box display="flex" alignItems="center" gap="8px">
                  {prescriptionStatus !== PrescriptionStatus.ISSUED && (
                    <PrescriptionStatusLabel status={prescriptionStatus} />
                  )}
                  {pdfData && !pdfData.treatment && (
                    <ButtonContainer>
                      <ShowPrescriptionButton
                        onClick={() =>
                          setDocumentData({
                            ...documentData,
                            documentId: id,
                            documentName: generatePdfName({
                              documentType: RequestType.PRESCRIPTION,
                              patient,
                              documentNameDetails: PrescriptionTypeMapper[type],
                            }),
                          })
                        }
                        disabled={
                          isError || !reducedPrescriptionData[id]?.pdfUrl
                        }
                      >
                        Ver receta
                      </ShowPrescriptionButton>
                    </ButtonContainer>
                  )}
                </Box>
              </Row>
              <PrescriptionDetailsContainer>
                {details.map((detail) => {
                  return (
                    <PrescriptionDetailCard key={detail.id} detail={detail} />
                  );
                })}
              </PrescriptionDetailsContainer>
            </Box>
          );
        })}
      </div>
    </SummaryContainer>
  );
};

HistoricPrescription.Skeleton = function HistoricPrescriptionSkeleton() {
  return (
    <SkeletonContainer>
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i}>
          <Row>
            <Skeleton variant="rectangular" height={20} width={160} />
          </Row>
          <PrescriptionDetailsContainer>
            <PrescriptionDetailCard.Skeleton />
          </PrescriptionDetailsContainer>
        </div>
      ))}
    </SkeletonContainer>
  );
};
